import React, { useMemo } from 'react';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import StationIcon from '@this/src/components/shared/icons/station_icon';
import AirportIcon from '@this/src/components/shared/icons/airport_icon';
import { Box } from '../../shared/box_parts';
import SelectTransportBoxRightInOutlineTemplate from '../right/right_in_outline.template';
import SelectTransportBoxRightInListTemplate from '../right/right_in_list.template';
import SelectTransportBoxRightInDomesticAirListTemplate from '../right/right_in_domestic_air_list.template';
import type SelectStore from '../../../../../domain/select_store';
import type Transit from '../../../../../domain/transit/transit';

interface DomesticTransportProps {
  transport: Transit;
  inList: boolean;
  selected: boolean;
  domesticAirPriceIndex: number;
  loading: boolean;
  handleDomesticAirPriceChange: (index: number) => void;
  handleChange: () => void;
  store: SelectStore;
  isRequestForm: boolean;
  disabled: boolean;
}

const DomesticTransport = ({
  transport,
  inList,
  selected,
  domesticAirPriceIndex,
  loading,
  handleDomesticAirPriceChange,
  handleChange,
  store,
  isRequestForm,
  disabled
}: DomesticTransportProps) => {
  const priceData = useMemo(() => {
    return transport.pricesOriginal?.[domesticAirPriceIndex];
  }, [transport.pricesOriginal, domesticAirPriceIndex]);
  const styles = useMemo(() => getStyles(), []);
  const showKabuyuLabel = !!store.user?.show_kabuyu_and_normal_fare_simultaneously;

  const renderRightContent = () => {
    if (inList === false) {
      return (
        <SelectTransportBoxRightInOutlineTemplate
          price={((): number | undefined => {
            if (transport.package) {
              return transport.additionalPrice || 0;
            }
            if (priceData) {
              return priceData.price;
            }
            return transport.price ?? undefined;
          })()}
          isAdditional={transport.package === true}
          priceRange={store.result.type === 'airPackage' ? transport.price_range ?? undefined : undefined}
          includeTax={transport.includeTax}
          selected={selected}
          onClick={handleChange}
          showsPricePerPersonText={store.result.type !== 'airPackage'}
          showsIncludeTax={store.result.type !== 'airPackage'}
          isRequestForm={isRequestForm || false}
          isRakutenPackage={transport.isRakutenPackage}
          disabled={store.result.type === 'airPackage' && store.isCurrentAirlinePackageLoading}
        />
      );
    }

    if (transport.package) {
      return (
        <SelectTransportBoxRightInListTemplate
          loading={loading}
          selected={selected}
          hovered={transport.hovered}
          priceRange={store.result.type === 'airPackage' ? transport.price_range ?? undefined : undefined}
          showsPricePerPersonText={store.result.type !== 'airPackage'}
          onClick={handleChange}
          onTouchStart={transport.handleTouched}
          onMouseOver={() => transport.setHovered(true)}
          onMouseLeave={() => transport.setHovered(false)}
          isPackage={transport.package}
          isDomestic={store.result.isDomesticAirResult()}
          disabled={disabled}
        />
      );
    }

    if (transport.airExists()) {
      return (
        <div style={{ ...styles.separateFlightSelect, padding: '5px 5px 0px 0px' }} className="flight">
          {transport.prices &&
            transport.prices.map((p, i) => (
              <div key={i} onClick={() => handleDomesticAirPriceChange(i)}>
                <SelectTransportBoxRightInDomesticAirListTemplate
                  key={i}
                  loading={loading}
                  selected={selected && priceData != null && p === priceData}
                  price={p}
                  showKabuyuLabel={showKabuyuLabel}
                />
              </div>
            ))}
        </div>
      );
    }

    return (
      <SelectTransportBoxRightInListTemplate
        loading={loading}
        selected={selected}
        hovered={transport.hovered}
        price={transport.price ?? undefined}
        isDomestic={store.result.isDomesticAirResult()}
        onClick={handleChange}
        onTouchStart={transport.handleTouched}
        onMouseOver={() => transport.setHovered(true)}
        onMouseLeave={() => transport.setHovered(false)}
      />
    );
  };

  return (
    <Box selected={selected}>
      <div style={styles.left}>
        {transport.segments.map(s =>
          s.legs.map((l, j) => (
            <div
              key={`${s.firstLegName}_${l.name}`}
              style={styles.segment}
              className="domestic-transport__segment"
            >
              <div style={styles.boxContent}>
                <div style={styles.legTime}>{l.from.time}</div>
                <div style={styles.boxTitle}>出発</div>
                <div style={styles.space} data-wovn-ignore>
                  <div style={styles.name}>{l.from.name === '' ? <Loading size="small" /> : l.from.name}</div>
                </div>
              </div>
              <div style={styles.boxContent}>
                <div style={styles.boxContent}>
                  <div style={styles.legTimeSpace} className="domestic-transport__leg-time-space" />
                  {s.type === 'shin' && <StationIcon />}
                  {s.type === 'air' && <AirportIcon />}
                </div>
                <div style={styles.spaceLeft}>
                  {l.airline_name && `${l.airline_name} `}
                  {l.name}
                </div>
                <img style={styles.image} src={s.imagePath()} alt="" />
              </div>
              <div style={styles.boxContent}>
                <div style={styles.legTime}>{l.to.time}</div>
                <div style={styles.boxTitle}>到着</div>
                <div style={styles.space} data-wovn-ignore>
                  <div style={styles.name}>{l.to.name === '' ? <Loading size="small" /> : l.to.name}</div>
                </div>
              </div>
              {inList === false && !transport.package && transport.airExists() && priceData && (
                <div style={styles.boxContent}>
                  <div style={styles.legTime} />
                  <div style={styles.boxTitle}>備考</div>
                  <div style={styles.space}>
                    {priceData?.ticket_category === 'changeable' ? '変更可' : '変更不可'}
                    &nbsp;&nbsp;&nbsp;
                    {priceData?.seat_type}
                    &nbsp;&nbsp;&nbsp;
                    {priceData?.seat_count &&
                      priceData.seat_count !== 'unknown' &&
                      (priceData.seat_count === '○' || Number(priceData.seat_count) >= 10
                        ? '10席以上'
                        : `${priceData.seat_count}席`)}
                  </div>
                </div>
              )}
              <div data-wovn-ignore className="select-transport-box__left__boarding-arrival">
                {s.shouldShowBoardingStation() && j === 0 && [`乗車駅：${s.boardingName()}`, <br key={0} />]}
                {s.shouldShowArrivalStation() && j === s.legs.length - 1 && `降車駅：${s.arrivalName()}`}
              </div>
            </div>
          ))
        )}
      </div>

      <div className={`select-transport-box__right-wrapper domestic ${inList ? '' : 'detail'}`}>
        {renderRightContent()}
      </div>
    </Box>
  );
};

const getStyles = () => ({
  left: {
    flexShrink: 10,
    padding: '2px 5px 2px 10px',
    margin: 'auto 0'
  } as const,
  segment: {
    // パディングは擬似セレクタが必要なためSCSSファイルで実装
  } as const,
  boxContent: {
    display: 'flex',
    alignItems: 'center',
    height: '2rem'
  } as const,
  name: {
    fontSize: '12px',
    fontWeight: 'bold'
  } as const,
  image: {
    height: '20px',
    marginLeft: '5px'
  } as const,
  boxTitle: {
    background: '#aaa',
    color: 'white',
    minWidth: '25px',
    marginRight: '10px',
    height: '16px',
    lineHeight: '16px',
    fontSize: '10px',
    fontWeight: 'bold',
    textAlign: 'center'
  } as const,
  space: {
    marginRight: '10px'
  } as const,
  spaceLeft: {
    marginLeft: '20px'
  } as const,
  legTime: {
    minWidth: '50px',
    color: '#3675ff',
    fontSize: '13px',
    height: '16px',
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: '0em',
    textAlign: 'left'
  } as const,
  legTimeSpace: {
    minWidth: '50px',
    color: '#3675ff',
    fontSize: '13px',
    height: '16px',
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: '0em',
    textAlign: 'left'
  } as const,
  separateFlightSelect: {
    width: '100%'
  } as const
});

export default DomesticTransport;
